import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { queryClient, trpc } from "../../app/trpc.js";
import { Button, Input, Notice, PageTitle } from "../../ui/index.js";
import { useCabinet } from "../cabinet/index.js";

/**
 * Демо-чат: владелец пишет как клиент и видит ответы бота. Тред создаётся при открытии и по кнопке «Новый диалог».
 * Без активного доступа (нет подписки) бот молчит: вместо тишины показываем уведомление со ссылкой на подписку и закрываем ввод.
 */
export function DemoChatPage() {
  const { t } = useTranslation();
  const { salonId, base } = useCabinet();
  const [threadId, setThreadId] = useState<string | null>(null);
  const [text, setText] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  const access = useQuery(trpc.bot.demo.access.queryOptions({ salonId }, { staleTime: 60_000, enabled: Boolean(salonId) }));
  const blocked = access.data?.access === "blocked";
  const start = useMutation(trpc.bot.demo.start.mutationOptions({ onSuccess: (r) => setThreadId(r.threadId) }));
  const messages = useQuery(trpc.bot.demo.messages.queryOptions(threadId ? { salonId, threadId } : skipToken, { refetchInterval: 1500 }));
  const send = useMutation(
    trpc.bot.demo.send.mutationOptions({
      onSuccess: () => {
        if (threadId) void queryClient.invalidateQueries(trpc.bot.demo.messages.queryFilter({ salonId, threadId }));
      },
    }),
  );

  // Новый тред при открытии страницы (и при смене салона)
  const startThread = start.mutate;
  useEffect(() => {
    startThread({ salonId });
  }, [startThread, salonId]);

  // Автопрокрутка вниз, когда приходит новое сообщение
  const items = messages.data?.messages ?? [];
  const lastId = items.at(-1)?.id;
  useEffect(() => {
    if (lastId && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [lastId]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || !threadId || send.isPending || blocked) return;
    // Поле очищаем сразу: то, что наберут во время отправки, не пропадёт.
    setText("");
    send.mutate({ salonId, threadId, text: value });
  };
  const onNewChat = () => {
    setThreadId(null);
    setText("");
    send.reset();
    start.mutate({ salonId });
  };

  const state = messages.data?.state;
  return (
    <div className="lg:max-w-3xl">
      <PageTitle>{t("demo.title")}</PageTitle>
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm text-slate-600">{t("demo.hint")}</p>
        {state && (
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${state === "escalated" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}
          >
            {t(`conversations.state.${state}`)}
          </span>
        )}
      </div>
      {(start.isError || messages.isError || send.isError || access.isError) && <Notice kind="error">{t("common.error")}</Notice>}
      {blocked && (
        <Notice kind="error">
          {t("demo.blocked")}{" "}
          <Link to={`${base}/billing`} className="font-medium underline">
            {t("demo.blockedAction")}
          </Link>
        </Notice>
      )}
      <div ref={listRef} className="my-3 h-[55dvh] space-y-2 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-3">
        {threadId && items.length === 0 && <div className="py-8 text-center text-sm text-slate-500">{t("demo.empty")}</div>}
        {items.map((m) => {
          if (m.author === "system") {
            return (
              <div key={m.id} className="text-center text-xs text-slate-500">
                {m.text}
              </div>
            );
          }
          const own = m.author === "client";
          const body = m.text;
          return (
            <div key={m.id} className={`flex flex-col ${own ? "items-end" : "items-start"}`}>
              {m.author === "staff" && <div className="mb-0.5 text-xs text-slate-500">{t("demo.staff")}</div>}
              <div
                className={`max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm ${
                  own ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-900"
                } ${!own && m.deliveryStatus === "pending" ? "opacity-60" : ""}`}
              >
                {body}
              </div>
              {!own && m.deliveryStatus === "pending" && <div className="mt-0.5 text-xs text-slate-400">{t("demo.sending")}</div>}
              {!own && m.deliveryStatus === "failed" && <div className="mt-0.5 text-xs text-red-600">{t("demo.failed")}</div>}
            </div>
          );
        })}
      </div>
      <form onSubmit={onSubmit} className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("demo.placeholder")} maxLength={2000} disabled={!threadId || blocked} />
        <Button type="submit" disabled={!threadId || blocked || send.isPending || !text.trim()}>
          {t("demo.send")}
        </Button>
      </form>
      <Button variant="ghost" className="mt-3 w-full lg:w-auto" onClick={onNewChat} disabled={start.isPending}>
        {t("demo.newChat")}
      </Button>
    </div>
  );
}
